<template>
  <component :is="tag"
             v-if="show"
             v-click-away="close"
             :style="style"
             tabindex="-1"
             :role="role"
             :aria-hidden="String(!show)"
             class="d-context-menu"
  >
    <slot :data="data" />
  </component>
</template>

<script>
  import { defineComponent } from 'vue';
  import { directive } from 'vue3-click-away';
  import {
    eventOff, eventOn, filterVisible, isArray, keyCodes, selectAll, setAttr,
  } from './_utils';

  /**
  * @displayName ContextMenu
  */
  export default defineComponent({
    name: 'DContextMenu',
    directives: {
      ClickAway: directive,
    },

    props: {
      closeOnClick: {
        type: Boolean,
        default: true,
      },
      closeOnScroll: {
        type: Boolean,
        default: true,
      },
      lazy: {
        type: Boolean,
        default: false,
      },
      itemSelector: {
        type: [String, Array],
        default: () => ['.v-context-item', '.v-context > li > a'],
      },
      role: {
        type: String,
        default: 'menu',
      },
      tag: {
        type: String,
        default: 'ul',
      },
    },
    emits: ['close', 'open'],

    data: (vm) => ({
      top: null,
      left: null,
      show: false,
      data: null,
      localItemSelector: vm.itemSelector,
    }),

    computed: {
      style() {
        return this.show
          ? { top: `${this.top}px`, left: `${this.left}px` }
          : null;
      },
    },

    watch: {
      closeOnScroll(newValue, oldValue) {
        if (newValue === oldValue) {
          return;
        }

        if (newValue && this.show) {
          this.addScrollEventListener();
        } else {
          this.removeScrollEventListener();
        }
      },
    },

    created() {
      if (isArray(this.localItemSelector)) {
        this.localItemSelector = this.localItemSelector
          .map((selector) => `${selector}:not(.disabled):not([disabled])`)
          .join(', ');
      }
    },

    beforeUnmount() {
      if (this.closeOnScroll) {
        this.removeScrollEventListener();
      }
    },

    methods: {
      addScrollEventListener() {
        eventOn(window, 'scroll', this.close);
      },

      close() {
        if (!this.show) {
          return;
        }

        this.resetData();

        if (this.closeOnScroll) {
          this.removeScrollEventListener();
        }

        this.$emit('close');
      },

      focusItem(index, items) {
        const el = items.find((el, idx) => idx === index);
        if (el && el.focus) {
          el.focus();
        }
      },

      focusNext(event, up) {
        if (!this.show) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        this.$nextTick(() => {
          const items = this.getItems();
          if (items.length < 1) {
            return;
          }

          let index = items.indexOf(event.target);
          if (up && index > 0) {
            index--;
          } else if (!up && index < items.length - 1) {
            index++;
          }

          if (index < 0) {
            index = 0;
          }

          this.focusItem(index, items);
        });
      },

      getItems() {
        return filterVisible(selectAll(this.localItemSelector, this.$el));
      },

      onClick() {
        this.close();
      },

      onKeydown(event) {
        const key = event.keyCode;

        if (key === keyCodes.ESC) {
          // Close on esc
          this.close();
        } else if (key === keyCodes.DOWN) {
          // Down arrow
          this.focusNext(event, false);
        } else if (key === keyCodes.UP) {
          // Up arrow
          this.focusNext(event, true);
        }
      },

      open(event, data) {
        this.data = data;
        this.show = true;

        this.$nextTick(() => {
          this.positionMenu(event.clientY, event.clientX);
          this.$el.focus();
          this.setItemRoles();

          if (this.closeOnScroll) {
            this.addScrollEventListener();
          }

          this.$emit('open', event, this.data, this.top, this.left);
        });
      },

      positionMenu(top, left) {
        const largestHeight = window.innerHeight - this.$el.offsetHeight - 25;
        const largestWidth = window.innerWidth - this.$el.offsetWidth - 25;

        if (top > largestHeight) {
          top = largestHeight;
        }

        if (left > largestWidth) {
          left = largestWidth;
        }

        this.top = top;
        this.left = left;
      },

      removeScrollEventListener() {
        eventOff(window, 'scroll', this.close);
      },

      resetData() {
        this.top = null;
        this.left = null;
        this.data = null;
        this.show = false;
      },

      setItemRoles() {
        // Add role="menuitem" and tabindex="-1" to all items
        selectAll(this.localItemSelector, this.$el)
          .forEach((el) => {
            setAttr(el, 'role', 'menuitem');
            setAttr(el, 'tabindex', '-1');
          });
      },
    },
  });
</script>